import * as React from "react";
import { AlertCircle, Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { useWorkspaceMembers } from "../../hooks/useWorkspaceMembers";
import { LoadingSpinner } from "./loadingSpinner/loadingSpinner";
import { Badge } from "./badge";

interface UserSearchComboboxProps {
  value: string[];
  onChange: (userIds: string[]) => void;
  placeholder?: string;
  emptyText?: string;
  /**
   * What to say when the read failed. Separate from `emptyText` because "no
   * users found" is a statement about the workspace, and a failed read has
   * established nothing about it.
   */
  errorText?: string;
  disabled?: boolean;
  className?: string;
  allowMultiple?: boolean;
  /** Set when rendered inside a modal Dialog: gives the portaled popover its
   * own focus scope so the dialog's focus trap doesn't steal the search input. */
  modal?: boolean;
}

export function UserSearchCombobox({
  value,
  onChange,
  placeholder = "Select users...",
  emptyText = "No users found.",
  errorText = "Could not load users.",
  disabled,
  className,
  allowMultiple = true,
  modal = false,
}: UserSearchComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");

  const { users, loading, error } = useWorkspaceMembers({ fetchOnLoad: true });

  const filteredUsers = React.useMemo(() => {
    if (!searchValue) return users;
    const lowerSearch = searchValue.toLowerCase();
    return users.filter((user) =>
      user.email.toLowerCase().includes(lowerSearch),
    );
  }, [users, searchValue]);

  const selectedUsers = React.useMemo(() => {
    return users.filter((user) => value.includes(user.id));
  }, [users, value]);

  const handleSelect = (selectedId: string) => {
    if (allowMultiple) {
      // Multi-select mode
      if (value.includes(selectedId)) {
        // Remove from selection
        onChange(value.filter((id) => id !== selectedId));
      } else {
        // Add to selection
        onChange([...value, selectedId]);
      }
    } else {
      // Single-select mode: replace selection and close popover
      onChange([selectedId]);
      setOpen(false);
    }
    setSearchValue("");
  };

  const handleRemove = (e: React.MouseEvent, userId: string) => {
    e.stopPropagation();
    onChange(value.filter((id) => id !== userId));
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal overflow-hidden min-h-10 h-auto",
            value.length === 0 && "text-stone-500",
            className,
          )}
        >
          <div className="flex flex-wrap gap-1 flex-1">
            {selectedUsers.length > 0 ? (
              allowMultiple ? (
                selectedUsers.map((user) => (
                  <Badge
                    key={user.id}
                    variant="secondary"
                    className="text-xs font-normal"
                  >
                    {user.email}
                    <X
                      className="ml-1 h-3 w-3 cursor-pointer hover:text-stone-900"
                      onClick={(e) => handleRemove(e, user.id)}
                    />
                  </Badge>
                ))
              ) : (
                <span className="truncate">{selectedUsers[0]?.email}</span>
              )
            ) : (
              <span>{placeholder}</span>
            )}
          </div>
          <div className="flex items-center gap-1 ml-2 shrink-0">
            {value.length > 0 && (
              <X
                className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                onClick={handleClearAll}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="min-w-[--radix-popover-trigger-width] max-w-md p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search users..."
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            {loading && filteredUsers.length === 0 ? (
              <div className="flex items-center justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : error !== null && filteredUsers.length === 0 ? (
              // Paired with an empty list the same way the spinner above is: a
              // refetch that fails over a list already on screen leaves the list
              // there rather than replacing an answer with a failure.
              <div className="flex items-start gap-2 py-2 px-4 text-sm text-red-600">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{errorText}</span>
              </div>
            ) : filteredUsers.length === 0 ? (
              <CommandEmpty className="text-sm text-center py-2 px-4">
                {emptyText}
              </CommandEmpty>
            ) : (
              <CommandGroup>
                {filteredUsers.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={user.id}
                    onSelect={() => handleSelect(user.id)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value.includes(user.id) ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span>{user.email}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
